import "./css/portIn.scss";
import { IPortinForm } from "@common/portIn/IPortinForm";
import { Grid, Typography, FormGroup, TextField, Button, IconButton, Box } from "@material-ui/core";
import { portInPhoneRegex } from "@utils/regex";
import { sendOtp } from "../../ApiRoutes";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ICartOffer } from "./ICart";
import { useEffect, useState } from "react";
import { useFetch } from "@common/hooks/useFetch";
import { IValidatePortInNumberResponse } from "@mappers/postProspectMapper";
import { VALIDATE_NUMBER_ROUTE } from "@api-routes";
import { IBooleanResponse } from "@common/IBooleanResponse";
import { ProviderDropdown } from "@common/portIn/ProviderDropdown";
import SubscriptionDropdown from "@common/portIn/SubscriptionDropdown";
import { getLanguage } from "@utils/changeLanguage";
import { keycloakUtils } from "src/itsf/keycloak/keycloakUtils";
import { EpicPostPaidModal } from "./EpicPostPaidModal";
import InfoIcon from "@material-ui/icons/Info";
import PortabilityInfoModal from "./PortabilityInfoModal";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Loader } from "@common/handleErrorAndLoading/Loader";

interface IParams {
    offer: ICartOffer;
    addPortin(cartOfferId: number, portinForm: IPortinForm, closePortInForm: () => void): void;
    closePortInForm(): void;
}

export const PortIn = (props: IParams) => {
    const { closePortInForm, offer, addPortin } = props;
    const { t } = useTranslation(["cart", "common", "portin", "validation", "customerDetails"]);
    const [_, { post }] = useFetch();
    const [showOTPResend, setShowOTPResend] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string>("");
    const [otpVerified, setOtpVerified] = useState<boolean>(false);
    const [isPrepaid, setIsPrepaid] = useState<boolean>(false);
    const [phoneNumberVerified, setPhoneNumberVerified] = useState<boolean>(false);
    const [isOfferPort, setIsOfferPort] = useState<boolean>(false);
    const [provider, setProvider] = useState<string>("");
    const [subscription, setSubscription] = useState<string>("");
    const [isPostPaidModalOpen, setIsPostPaidModalOpen] = useState<boolean>(false);
    const [isPortabilityInfoModalOpen, setIsPortabilityInfoModalOpen] = useState<boolean>(false);
    const [modalPhoneNumber, setModalPhoneNumber] = useState<string>("");
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [isValidateLoading, setIsValidateLoading] = useState<boolean>(false);

    const formMethods = useForm<IPortinForm>({
        defaultValues: {
            phoneNumber: offer.port?.msisdn || "",
            provider: offer.port?.operator || "",
            portabilityCode: offer.port?.code || "",
            currentSubscription: offer.port?.serviceType || "",
        },
    });

    const { handleSubmit, errors, register, getValues, control, setError, clearErrors, setValue } = formMethods;
    const otpValue = useWatch<string>({ name: "portabilityCode", control: control });

    useEffect(() => {
        if (offer.port) setIsOfferPort(true);
    }, []);

    const sendOtpFunction = async () => {
        if (!executeRecaptcha) {
            console.error("portin sendOtpFunction - reCAPTCHA hasn't loaded yet");
            return;
        }

        const recaptchaToken = await executeRecaptcha("resendOTPPortin");

        post<IBooleanResponse>(sendOtp(), {
            body: {
                receiver: getValues("phoneNumber"),
                type: "MSISDN",
                lang: getLanguage(),
                recaptchaToken: recaptchaToken,
            },
            isSecured: keycloakUtils.isAuthenticated(),
        });
    };

    const validatePhoneNumberOnClick = async () => {
        if (await formMethods.trigger("phoneNumber")) {
            if (!executeRecaptcha) {
                console.error("validatePhoneNumberOnClick - reCAPTCHA hasn't loaded yet");
                return;
            }

            setIsValidateLoading(true);

            const number: string = getValues("phoneNumber");
            let errorMessage = t("portin:somethingWentWrong");

            const recaptchaToken = await executeRecaptcha("validatePortin");

            await post<IValidatePortInNumberResponse>(VALIDATE_NUMBER_ROUTE, {
                body: {
                    msisdn: number,
                    lang: getLanguage(),
                    recaptchaToken: recaptchaToken,
                },
                isSecured: keycloakUtils.isAuthenticated(),
            })
                .then((response) => {
                    if (response) {
                        if (response.postpaid && !response.success) {
                            errorMessage = t("portin:epicPostPaidError");
                            setModalPhoneNumber(number);
                            setIsPostPaidModalOpen(true);
                        } else if (response.pendingPortIn) {
                            errorMessage = t("portin:pendingPortInError");
                        } else if (response.prepaid && response.success) {
                            errorMessage = "";
                            setIsPrepaid(true);
                        } else if (
                            response.success &&
                            !response.pendingPortIn &&
                            !response.postpaid &&
                            !response.prepaid
                        ) {
                            errorMessage = "";
                        }
                    }
                })
                .catch((error) => {
                    console.error("validatePhoneNumberOnClick OTP verification failed:", error);
                    errorMessage = t("validation:validateNumberFailed");
                });

            setValidationError(errorMessage);
            setPhoneNumberVerified(errorMessage ? false : true);
            setIsValidateLoading(false);
        }
    };

    const handleValidateOTP = async () => {
        await post<IBooleanResponse>(sendOtp() + "/verify", {
            body: {
                receiver: getValues("phoneNumber"),
                otp: getValues("portabilityCode"),
                type: "MSISDN",
            },
            isSecured: keycloakUtils.isAuthenticated(),
        }).then((response) => {
            if (response?.result) {
                setOtpVerified(true);
                clearErrors("portabilityCode");
            } else {
                setOtpVerified(false);
                setError("portabilityCode", { message: t("portin:invalidPortabilityCode") });
                setShowOTPResend(true);
            }
        });
    };

    const handleResendOTP = async () => {
        setValue("portabilityCode", "");
        clearErrors("portabilityCode");
        setShowOTPResend(false);
        setOtpVerified(false);
        await sendOtpFunction();
    };

    const submitData = (portinFormData: IPortinForm) => {
        addPortin(
            offer.offerId,
            { ...portinFormData, phoneNumber: getValues("phoneNumber"), portabilityCode: getValues("portabilityCode") },
            closePortInForm
        );
    };

    const PostPaidPortabilityInfo = () => {
        const handleMouseEnter = () => {
            setIsPortabilityInfoModalOpen(true);
        };

        return (
            <Box display="flex" alignItems="center">
                <Typography color="secondary" variant="body1">
                    {t("portin:postPaidCode")}
                </Typography>
                <IconButton
                    color="secondary"
                    onMouseEnter={handleMouseEnter}
                    onTouchStart={handleMouseEnter}
                    disableRipple={true}
                    size="small"
                >
                    <InfoIcon />
                </IconButton>
            </Box>
        );
    };

    return (
        <div className="portInForm-container">
            <div className="portInForm-container__circle" />
            <form className="xtest_portin_form portInForm" onSubmit={handleSubmit(submitData)}>
                <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <Typography color="secondary" variant="body1">
                            {t("cart:portInInfo")}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <section className="form-section">
                            <FormGroup row>
                                <Grid container spacing={3} alignItems="center">
                                    <Grid item md={4} sm={6} xs={12}>
                                        <TextField
                                            error={Boolean(errors.phoneNumber?.message)}
                                            helperText={errors.phoneNumber?.message}
                                            fullWidth
                                            disabled={phoneNumberVerified || isOfferPort}
                                            id="phoneNumber"
                                            inputRef={register({
                                                required: t("validation:required"),
                                                pattern: {
                                                    value: portInPhoneRegex,
                                                    message: t("portin:phoneNumberError"),
                                                },
                                            })}
                                            label={`${t("portin:phoneNumber")} *`}
                                            margin="dense"
                                            name="phoneNumber"
                                            placeholder="9XXXXXXX"
                                            type="text"
                                        />
                                    </Grid>
                                    {!phoneNumberVerified && !isOfferPort && (
                                        <Grid item md={3} sm={6} xs={12}>
                                            {isValidateLoading && <Loader size={40} />}
                                            {!isValidateLoading && (
                                                <Button
                                                    color="primary"
                                                    fullWidth
                                                    id="getCodeButton"
                                                    type="button"
                                                    variant="contained"
                                                    onClick={validatePhoneNumberOnClick}
                                                >
                                                    {t("portin:validate")}
                                                </Button>
                                            )}
                                        </Grid>
                                    )}
                                </Grid>
                            </FormGroup>
                            {validationError && validationError !== t("portin:epicPostPaidError") && (
                                <Typography color="error" variant="body1">
                                    {validationError}
                                </Typography>
                            )}
                            {(phoneNumberVerified || isOfferPort) && (
                                <FormGroup className="portInForm__formGroup" row>
                                    <Grid container>
                                        <Grid container spacing={3}>
                                            <Grid item md={4} sm={6} xs={12}>
                                                <ProviderDropdown
                                                    error={errors?.provider}
                                                    control={control}
                                                    isOfferPort={isOfferPort}
                                                    setProvider={setProvider}
                                                    isPrepaid={isPrepaid}
                                                />
                                            </Grid>
                                            <Grid item md={4} sm={6} xs={12}>
                                                <SubscriptionDropdown
                                                    error={errors?.currentSubscription}
                                                    control={control}
                                                    isOfferPort={isOfferPort}
                                                    provider={provider}
                                                    setSubscription={setSubscription}
                                                    isPrepaid={isPrepaid}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {isPrepaid ? (
                                        !isOfferPort ? (
                                            <Grid container spacing={3} alignItems="center">
                                                <Grid item md={4} sm={6} xs={12}>
                                                    <TextField
                                                        disabled={isOfferPort || otpVerified}
                                                        error={Boolean(errors.portabilityCode?.message)}
                                                        helperText={errors.portabilityCode?.message}
                                                        fullWidth
                                                        id="portabilityCode"
                                                        inputRef={register({
                                                            required: t("validation:required"),
                                                        })}
                                                        label={`${t("portin:portabilityCode")} *`}
                                                        margin="dense"
                                                        name="portabilityCode"
                                                        type="text"
                                                    />
                                                </Grid>
                                                {!otpVerified && (
                                                    <Grid item md={3} sm={6} xs={12}>
                                                        <Button
                                                            disabled={!otpValue}
                                                            color="primary"
                                                            fullWidth
                                                            id="validatePortabilityButton"
                                                            type="button"
                                                            variant="contained"
                                                            onClick={handleValidateOTP}
                                                        >
                                                            {t("portin:validate")}
                                                        </Button>
                                                    </Grid>
                                                )}
                                                {showOTPResend && (
                                                    <Grid item md={2} xs={12}>
                                                        <Button
                                                            color="primary"
                                                            type="button"
                                                            variant="contained"
                                                            onClick={handleResendOTP}
                                                        >
                                                            {t("portin:resendOTP")}
                                                        </Button>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        ) : (
                                            <></>
                                        )
                                    ) : (
                                        <Grid item md={4} sm={6} xs={12}>
                                            <TextField
                                                disabled={isOfferPort}
                                                error={Boolean(errors.portabilityCode?.message)}
                                                helperText={errors.portabilityCode?.message}
                                                fullWidth
                                                id="portabilityCode"
                                                inputRef={register({
                                                    required: t("validation:required"),
                                                })}
                                                label={`${t("portin:portabilityCode")} *`}
                                                margin="dense"
                                                name="portabilityCode"
                                                type="text"
                                            />
                                        </Grid>
                                    )}
                                    {provider && subscription && (
                                        <Grid item xs={12}>
                                            {subscription === "PS" ? (
                                                <PostPaidPortabilityInfo />
                                            ) : (
                                                <Typography color="secondary" variant="body1">
                                                    {t(`portin:${provider}CodeText` as any)}
                                                </Typography>
                                            )}
                                        </Grid>
                                    )}
                                </FormGroup>
                            )}
                        </section>
                        <section className="portInForm-submit">
                            <Grid className="portInForm-submit" container justify="flex-end" spacing={2}>
                                <Grid item md={2} sm={3} xs={4}>
                                    <Button
                                        className="xtest_cancel_button"
                                        color="primary"
                                        fullWidth
                                        id="cancelButton"
                                        size="large"
                                        variant="outlined"
                                        onClick={closePortInForm}
                                    >
                                        {isOfferPort ? t("common:close") : t("common:cancel")}
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        disabled={isOfferPort || (isPrepaid && !otpVerified) || !phoneNumberVerified}
                                        className="xtest_submit_button"
                                        color="primary"
                                        fullWidth
                                        id="submitButton"
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                    >
                                        {t("common:continue")}
                                    </Button>
                                </Grid>
                            </Grid>
                        </section>
                    </Grid>
                </Grid>
            </form>
            {isPostPaidModalOpen && (
                <EpicPostPaidModal
                    close={() => {
                        setIsPostPaidModalOpen(false);
                    }}
                    isPostPaidModalOpen={isPostPaidModalOpen}
                    mobileNumber={modalPhoneNumber}
                    setIsPostPaidModalOpen={setIsPostPaidModalOpen}
                />
            )}
            {isPortabilityInfoModalOpen && (
                <PortabilityInfoModal
                    isModalOpen={isPortabilityInfoModalOpen}
                    provider={provider}
                    close={() => {
                        setIsPortabilityInfoModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};
